import type { Page, Pageable } from '../../../core/api/spring-types';
import { normalizePage } from '../../../core/api/spring-types';
import type {
    SchoolRequestReportDto,
    SchoolRequestDto,
    SchoolRequestApprovalDto,
    SchoolRequestReassignmentDto,
    SchoolFormConfirmationDto,
    IstoricProfesorDto
} from '../../../api/generated/types.gen';
import {
    getSchoolRequestsReport,
    getSchoolRequestById,
    getSchoolRequestByUserId,
    getOwnSchoolRequest,
    getDefaultSchoolRequest,
    createSchoolRequest,
    updateSchoolRequest,
    deleteSchoolRequest,
    updateSchoolRequestApprovalStatus,
    reassignSchoolRequestToProfessor,
    confirmSchoolRequestUpdate,
    updateSchoolRequestByAdmin,
    getIstoricProfesori,
    // 🔴 Alias: numele generat spune „mark … video seen" fara subiect, iar fisierul asta
    //    are deja douasprezece functii importate. Se importa cu alias si nu se foloseste
    //    direct nicaieri altundeva.
    markOwnSchoolRequestVideoSeen as marcheazaVideoVazutGenerat,
} from '../../../api/generated/sdk.gen';
import { handleResponse } from '@/core/api/response-handler';

// Export types for external use
export type {
    SchoolRequestReportDto,
    SchoolRequestDto,
    SchoolRequestApprovalDto,
    SchoolRequestReassignmentDto,
    SchoolFormConfirmationDto,
    IstoricProfesorDto
};

/**
 * School Requests API - Thin wrapper for auto-generated SDK functions
 * Maintains backward compatibility with existing hook signatures
 */
class SchoolRequestsAPI {
    // Report endpoint
    async getReport(
        filter: Partial<SchoolRequestReportDto>,
        pageable: Pageable
    ): Promise<Page<SchoolRequestReportDto>> {
        const response = await getSchoolRequestsReport({
            query: { ...filter, ...pageable } as any,
        });
        return normalizePage(handleResponse(response));
    }

    // CRUD operations
    async getById(id: string): Promise<SchoolRequestDto> {
        const response = await getSchoolRequestById({
            path: { id },
        });
        return handleResponse(response);
    }

    async getByUserId(userId: string): Promise<SchoolRequestDto> {
        const response = await getSchoolRequestByUserId({
            path: { id: userId },
        });
        return handleResponse(response);
    }

    /**
     * Cererea proprie, sau `null` cand profesorul nu are inca niciuna.
     *
     * 🔴 „Fara cerere" e **204 fara corp** (din 2026-10-08), nu 404. Pana atunci era 404, adica o
     * stare normala a fluxului iesea ca eroare in telemetrie si in consola. Un 404 pe ruta asta
     * inseamna acum altceva — ruta lipseste — si iese ca eroare prin `handleResponse`, ca orice 4xx.
     *
     * ⚠️ `handleResponse` NU intoarce `undefined` la 204: axios lasa corpul gol ca `''`, iar clientul
     * generat face `data ?? {}` (deci `{}` doar pentru `null`/`undefined`). De-aia se verifica si
     * codul, si forma corpului — un sir gol sau un obiect fara chei nu e o cerere.
     */
    async getOwn(): Promise<SchoolRequestDto | null> {
        const response = await getOwnSchoolRequest();
        const data: unknown = handleResponse(response);
        if (response.status === 204) return null;
        if (typeof data !== 'object' || data === null || Object.keys(data).length === 0) return null;
        return data as SchoolRequestDto;
    }

    async getDefault(): Promise<SchoolRequestDto> {
        const response = await getDefaultSchoolRequest();
        return handleResponse(response);
    }

    /**
     * Marcheaza materialul video ca vazut pe propria cerere de inscriere (SP-03).
     *
     * 🔴 `handleResponse` nu e formalitate aici: SDK-ul generat NU arunca la eroare, il
     * intoarce (`ThrowOnError = false`). Fara el, un 404 (profesor fara cerere in editia
     * curenta) ar trece drept succes, iar noi am crede ca serverul a scris ceva ce n-a
     * scris — marcaj inexistent, cu poarta „trecuta".
     *
     * Ruta e idempotenta si pastreaza PRIMUL moment, deci un al doilea apel intoarce tot
     * 200 cu aceeasi valoare. Santinela de pe pagina exista ca sa nu batem in server
     * degeaba, nu ca sa protejam serverul.
     *
     * ⚠️ **Cerere deja decisa (APPROVED / REJECTED) = 200, nu 409** — din #97 (2026-09-05).
     * Raspunsul e starea curenta, fara scriere. 409-ul a fost scos pentru ca trecea prin
     * `GlobalExceptionHandler` si lasa un rand in `discord_error_logs` la fiecare atingere:
     * un incident fals pentru o stare normala a fluxului. Consecinta pentru codul de aici:
     * cazul ala NU mai ajunge in `onError`, ci in `onSuccess`, si nu lasa nicio urma —
     * ceea ce e in regula cat timp consemnarea e de fundal, si inceteaza sa fie in ziua in
     * care devine poarta tare (vezi limita tacerii de langa hook).
     *
     * ⚠️ Raspunsul de no-op poate veni cu `videoVazutDt` LIPSA (profesor aprobat care n-a
     * vazut niciodata materialul). Nu e o eroare de citit — e exact cazul pentru care
     * exista regula de afisare de mai jos.
     *
     * ⚠️ `videoVazutDt` din raspuns e string ISO, oricat ar spune tipul generat ca e
     * `Date` — transformerele nu ruleaza niciodata. Daca ajunge sa se afiseze:
     * `formatUnixTimestamp()`, nu `.getTime()`.
     *
     * 🔴 SI, MAI IMPORTANT, regula de afisare (S1): o data randata pe ecran spune
     * „serverul stie asta" fara ca nimeni sa scrie fraza — deci afirmatia despre server
     * nu mai vine din text, vine din DATE. De aceea:
     *   · se afiseaza DOAR daca `videoVazutDt` chiar exista in raspuns;
     *   · NICIODATA o rezerva de tipul „azi" sau data locala cand campul lipseste —
     *     exact acolo s-ar naste o minciuna care arata identic cu adevarul;
     *   · daca lipseste, nu se scrie nimic: absenta E informatia corecta.
     * Acelasi tipar ca bifa „descarcat" de la protocol, pe care owner-ul a refuzat-o:
     * ambele ar afirma despre server ceva ce clientul n-are de unde sti direct.
     */
    async marcheazaVideoVazut(): Promise<SchoolRequestDto> {
        const response = await marcheazaVideoVazutGenerat();
        return handleResponse(response);
    }

    async create(data: SchoolRequestDto): Promise<SchoolRequestDto> {
        const response = await createSchoolRequest({
            body: data,
        });
        return handleResponse(response);
    }

    async update(data: SchoolRequestDto): Promise<SchoolRequestDto> {
        const response = await updateSchoolRequest({
            body: data,
        });
        return handleResponse(response);
    }

    async delete(id: string): Promise<void> {
        const response = await deleteSchoolRequest({
            path: { id },
        });
        handleResponse(response);
    }

    // Special operations
    async updateApprovalStatus(data: SchoolRequestApprovalDto): Promise<SchoolRequestDto> {
        const response = await updateSchoolRequestApprovalStatus({
            body: data,
        });
        return handleResponse(response);
    }

    async reassignProfessor(data: SchoolRequestReassignmentDto): Promise<SchoolRequestDto> {
        const response = await reassignSchoolRequestToProfessor({
            body: data,
        });
        return handleResponse(response);
    }

    async confirmUpdate(data: SchoolFormConfirmationDto): Promise<SchoolRequestDto> {
        const response = await confirmSchoolRequestUpdate({
            body: data,
        });
        return handleResponse(response);
    }

    async updateByAdmin(data: SchoolRequestDto): Promise<void> {
        const response = await updateSchoolRequestByAdmin({
            body: data,
        });
        handleResponse(response);
    }

    /** B4: istoricul profesorilor unei scoli (DBEST/ADMIN). */
    async getIstoricProfesori(codSiiirUnitate: string): Promise<IstoricProfesorDto[]> {
        const response = await getIstoricProfesori({ query: { codSiiirUnitate } });
        return handleResponse(response);
    }
}

export const schoolRequestsApi = new SchoolRequestsAPI();
